'use client';

import { cn } from '@/lib/utils';
import { Root as Dialog, Close as DialogClose, Portal as DialogPortal, Content as DialogPrimitiveContent, Description as DialogPrimitiveDescription, Overlay as DialogPrimitiveOverlay, Title as DialogPrimitiveTitle, Trigger as DialogTrigger } from '@radix-ui/react-dialog';
import React from 'react';
import Draggable from 'react-draggable';
import { IoClose } from 'react-icons/io5';
type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitiveContent> & {
  removeClose?: boolean;
};
const DialogOverlay = React.forwardRef<React.ElementRef<typeof DialogPrimitiveOverlay>, React.ComponentPropsWithoutRef<typeof DialogPrimitiveOverlay>>(({
  className,
  ...props
}, ref) => <DialogPrimitiveOverlay ref={ref} className={cn('fixed inset-0 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0', className)} {...props} />);
DialogOverlay.displayName = DialogPrimitiveOverlay.displayName;
const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitiveContent>, DialogContentProps>(({
  className,
  children,
  removeClose,
  ...props
}, ref) => <DialogPortal>
      <DialogOverlay />
      <div id="dialog-container" className="fixed inset-0 z-[111]">
        <Draggable bounds="parent" defaultPosition={{
      x: typeof window !== 'undefined' ? window.innerWidth / 2 - 300 : 0,
      // centers horizontally (assuming max-w-lg ≈ 600px)
      y: typeof window !== 'undefined' ? window.innerHeight / 2 - 200 : 0 // centers vertically
    }}>
          <DialogPrimitiveContent ref={ref} className={cn('absolute z-50 grid w-full max-w-lg cursor-move gap-4 border border-[#ababab] bg-white p-6 shadow-lg duration-200 sm:rounded-lg', className)} onInteractOutside={e => e.preventDefault()} {...props}>
            {children}
            {!removeClose && <DialogClose className="absolute right-4 top-[20px]">
                <div className="flex h-4 w-4 items-center justify-center rounded-3xl bg-[#F05959]">
                  <IoClose className="h-3 w-3 fill-[#fff]" />
                </div>
                <span className="sr-only">Close</span>
              </DialogClose>}
          </DialogPrimitiveContent>
        </Draggable>
      </div>
    </DialogPortal>);
DialogContent.displayName = DialogPrimitiveContent.displayName;
const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => <div className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)} {...props} data-sentry-component="DialogHeader" data-sentry-source-file="dialog.tsx" />;
DialogHeader.displayName = 'DialogHeader';
const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...props} data-sentry-component="DialogFooter" data-sentry-source-file="dialog.tsx" />;
DialogFooter.displayName = 'DialogFooter';
const DialogTitle = React.forwardRef<React.ElementRef<typeof DialogPrimitiveTitle>, React.ComponentPropsWithoutRef<typeof DialogPrimitiveTitle>>(({
  className,
  ...props
}, ref) => <DialogPrimitiveTitle ref={ref} className={cn('text-[16px] font-semibold leading-none tracking-tight text-[#212121]', className)} {...props} />);
DialogTitle.displayName = DialogPrimitiveTitle.displayName;
const DialogDescription = React.forwardRef<React.ElementRef<typeof DialogPrimitiveDescription>, React.ComponentPropsWithoutRef<typeof DialogPrimitiveDescription>>(({
  className,
  ...props
}, ref) => <DialogPrimitiveDescription ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />);
DialogDescription.displayName = DialogPrimitiveDescription.displayName;
export { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger };